import type { Dispatch } from "react";
import { FormControlLabel, Switch } from "@mui/material";

import { EditableLabel } from "@/features/widget-forms/forms/weather-model-map/components/editable-label";
import DraggableCard, {
  configItemCardStyles,
} from "@/features/widget-forms/forms/weather-model-map/components/draggable-card/draggable-card";
import { StaticOverlayColorSelect } from "@/features/widget-forms/forms/weather-model-map/components/static-overlay-color-select";
import { StaticOverlayColorSwatch } from "@/features/widget-forms/forms/weather-model-map/components/static-overlay-color-swatch";
import type {
  StaticMapOverlaysOverlayBase,
  StaticMapOverlaysOverlayConfiguration,
} from "@/lib/api";
import type { StaticOverlayColor } from "@/features/shared/widgets/model-map/static-overlay-color-presets";
import { useTranslation } from "@/lib/i18n";

import styles from "./static-overlay-card.module.css";

interface StaticOverlayCardProps {
  config: StaticMapOverlaysOverlayConfiguration;
  overlayInfo?: StaticMapOverlaysOverlayBase;
  onRename: Dispatch<string>;
  onColorChange: Dispatch<StaticOverlayColor>;
  onEnabledByDefaultChange: Dispatch<boolean>;
  onRemove: () => void;
}

function StaticOverlayCard({
  config,
  overlayInfo,
  onRename,
  onColorChange,
  onEnabledByDefaultChange,
  onRemove,
}: StaticOverlayCardProps) {
  const { t } = useTranslation();
  const originalTitle = overlayInfo?.name;
  const subtitle = originalTitle && originalTitle !== config.label ? originalTitle : "";

  return (
    <DraggableCard
      onRemove={onRemove}
      removeLabel={t("admin.widgets.fields.model_map.general.remove_item", {
        item: config.label,
      })}
    >
      <div className={configItemCardStyles.titleGroup}>
        <div className={configItemCardStyles.title}>
          <EditableLabel
            value={config.label}
            onValueChange={onRename}
            placeholder={originalTitle}
          />
        </div>
        {subtitle ? <div className={configItemCardStyles.subtitle}>{subtitle}</div> : null}
        <div className={styles.colorRow}>
          <div className={styles.swatchRow}>
            <StaticOverlayColorSwatch value={config.color} />
            <div className={styles.colorSelectGrow}>
              <StaticOverlayColorSelect
                value={config.color}
                onValueChange={onColorChange}
              />
            </div>
          </div>
        </div>
        <div className={styles.defaultVisibleRow}>
          <FormControlLabel
            control={
              <Switch
                checked={config.enabled_by_default}
                onChange={(_, checked) => onEnabledByDefaultChange(checked)}
                color="primary"
              />
            }
            label={t("admin.widgets.fields.map.layer.enabledByDefault")}
          />
        </div>
      </div>
    </DraggableCard>
  );
}

export default StaticOverlayCard;
